import React, { useState, useEffect } from 'react';
import Footer from './Footer';
import Header from './Header';
import { Globe, Smartphone, ChevronRight, CheckCircle, BarChart2, Bell, TrendingUp, Award, Shield, Zap, Lock } from 'lucide-react';

export const SafeCredits = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id^="feature-"]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <BarChart2 className="w-6 h-6" />,
      text: "Business credit scores and explainable credit reports for MSMEs and distributors",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      text: "Digital onboarding with GST, CIN, bank statements, and structured KYC",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      text: "Cash flow analysis including debtors days, creditors days, and cash conversion cycle",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      text: "Early warning signals for delayed payments and financial stress",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      text: "Distributor onboarding, credit limit decisions, and ongoing credit governance",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: <Award className="w-6 h-6" />,
      text: "Insights that help enterprises reduce risk and improve collections and profitability",
      color: "from-rose-500 to-pink-600"
    }
  ];

  return (
    <div className="w-full min-h-screen bg-slate-950 text-white relative overflow-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
        
        * {
          font-family: 'Outfit', sans-serif;
        }
        
        .mono {
          font-family: 'JetBrains Mono', monospace;
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes gradient-rotate {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        .animate-in {
          animation: fade-in-up 0.8s ease forwards;
        }
        
        .feature-card {
          opacity: 0;
          transform: translateY(30px);
        }
        
        .feature-card.visible {
          animation: fade-in-up 0.6s ease forwards;
        }
        
        .glass-dark {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .shimmer-effect {
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          background-size: 200% 100%;
          animation: shimmer 3s infinite;
        }
        
        .gradient-border {
          position: relative;
          background: rgba(15, 23, 42, 0.5);
        }
        
        .gradient-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6);
          border-radius: inherit;
          z-index: -1;
          animation: gradient-rotate 4s linear infinite;
        }
        
        .text-gradient-safe {
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f0abfc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .grid-pattern {
          background-image: 
            linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.3;
          animation: pulse-glow 8s ease-in-out infinite;
        }
      `}</style>

      {/* Animated Background Orbs */}
      <div className="orb w-96 h-96 bg-blue-500 top-20 -left-48" style={{animationDelay: '0s'}}></div>
      <div className="orb w-80 h-80 bg-purple-500 top-1/3 -right-40" style={{animationDelay: '2s'}}></div>
      <div className="orb w-72 h-72 bg-cyan-500 bottom-1/4 left-1/4" style={{animationDelay: '4s'}}></div>
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      
      <Header/>
      
      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        {/* Hero Section */}
        <div className="flex flex-col lg:flex-row items-center gap-16 mb-24">
          <div className="lg:w-3/5 space-y-8 animate-in">
            <div className="inline-flex items-center gap-2 glass-dark px-5 py-2.5 rounded-full text-sm font-semibold text-blue-300 shadow-sm">
              <Shield size={16} className="text-blue-400" />
              <span className="mono">B2B FINTECH PLATFORM</span>
            </div>
            
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none">
                <span className="text-gradient-safe">SafeCredits</span>
              </h1>
              
              <div className="space-y-3">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-200">
                  Empowering Growth.
                  <br />
                  <span className="text-blue-400">Safeguarding Trust.</span>
                </h2>
                
                <div className="w-24 h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full"></div>
              </div>
            </div>
            
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl">
              SafeCredits is a <span className="font-semibold text-slate-200">B2B fintech platform</span> built for enterprises, 
              distributors, and MSMEs in India. We help organizations onboard businesses digitally, assess creditworthiness, 
              and monitor financial risk using <span className="font-semibold text-blue-400">verified data</span>, explainable insights, 
              and real-time intelligence.
            </p>
            
            <div className="flex flex-wrap gap-4 pt-4">
              <a 
                href="https://www.safecredits.in" 
                className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-2xl hover:shadow-blue-500/50 transition-all hover:scale-105 overflow-hidden"
              >
                <span className="relative z-10">Visit Our Website</span>
                <Globe className="w-5 h-5 relative z-10" />
                <ChevronRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </a>
              
              <button className="inline-flex items-center gap-3 glass-dark px-8 py-4 rounded-xl font-semibold text-lg text-slate-200 hover:bg-white/10 transition-all">
                <Smartphone className="w-5 h-5" />
                <span>Request Demo</span>
              </button>
            </div>
          </div>
          
          <div className="lg:w-2/5 flex justify-center" style={{animation: 'fade-in-up 0.8s ease 0.2s backwards'}}>
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/30 to-purple-600/30 rounded-3xl blur-2xl opacity-50" style={{animation: 'float-gentle 6s ease-in-out infinite'}}></div>
              
              {/* Main logo container */}
              <div className="relative glass-dark rounded-3xl p-12 shadow-2xl border border-white/10" style={{animation: 'float-gentle 8s ease-in-out infinite'}}>
                <div className="absolute inset-0 shimmer-effect rounded-3xl opacity-0 hover:opacity-100 transition-opacity"></div>
                <img 
                  src='Safe Credits Logo White.png' 
                  className='w-72 h-72 object-contain drop-shadow-2xl relative z-10' 
                  alt="SafeCredits Logo"
                />
              </div>
              
              {/* Floating accent elements */}
              <div className="absolute -top-6 -right-6 glass-dark p-4 rounded-2xl shadow-lg border border-white/10" style={{animation: 'float-gentle 5s ease-in-out infinite 0.5s'}}>
                <Lock className="w-7 h-7 text-blue-400" />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-dark p-4 rounded-2xl shadow-lg border border-white/10" style={{animation: 'float-gentle 6s ease-in-out infinite 1s'}}>
                <Zap className="w-7 h-7 text-purple-400" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Bar */}
        <div className="glass-dark rounded-2xl p-8 mb-24 shadow-lg border border-white/10" style={{animation: 'fade-in-up 0.8s ease 0.4s backwards'}}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { label: "Real-Time", value: "Intelligence", icon: <TrendingUp className="w-6 h-6" /> },
              { label: "Verified", value: "Data Sources", icon: <CheckCircle className="w-6 h-6" /> },
              { label: "Explainable", value: "Credit Reports", icon: <BarChart2 className="w-6 h-6" /> }
            ].map((stat, index) => (
              <div key={index} className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl text-white">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-3xl font-bold text-gradient-safe">{stat.value}</div>
                  <div className="text-sm text-slate-400 mono">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Features and Quote Section */}
        <div className="flex flex-col lg:flex-row gap-12 mb-24">
          {/* Features */}
          <div className="lg:w-1/2">
            <div className="mb-12" style={{animation: 'fade-in-up 0.8s ease 0.6s backwards'}}>
              <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-blue-300 mb-4">
                <Award size={16} />
                <span className="mono">POWERFUL FEATURES</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-100">
                Everything You Need to
                <br />
                <span className="text-gradient-safe">Manage Credit Risk</span>
              </h2>
            </div>
            
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  id={`feature-${index}`}
                  className={`feature-card group ${isVisible[`feature-${index}`] ? 'visible' : ''}`}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="glass-dark rounded-2xl p-6 hover:bg-white/5 hover:shadow-2xl hover:shadow-blue-500/20 transition-all border border-white/10 relative overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    
                    <div className="flex items-start gap-4 relative z-10">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                        {React.cloneElement(feature.icon, { className: 'w-6 h-6' })}
                      </div>
                      <p className="text-slate-400 leading-relaxed pt-2 group-hover:text-slate-300 transition-colors">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Quote and CTA */}
          <div className="lg:w-1/2 flex flex-col gap-8" style={{animation: 'fade-in-up 0.8s ease 0.8s backwards'}}>
            {/* Quote Card */}
            <div className="glass-dark rounded-3xl p-10 shadow-xl border border-white/10 relative overflow-hidden group flex-1">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10 space-y-6 text-slate-200 group-hover:text-white transition-colors duration-500">
                <div className="text-7xl font-serif text-blue-400 group-hover:text-white opacity-20 leading-none">"</div>
                
                <blockquote className="text-2xl font-medium leading-relaxed italic">
                  SafeCredits is built to bring transparency, discipline, and intelligence into how
                  businesses onboard partners, extend credit, and manage financial risk at scale.
                </blockquote>
                
                <div className="flex items-center gap-3">
                  <div className="w-16 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"></div>
                  <span className="mono text-xs text-slate-400 group-hover:text-white/80">SAFECREDITS VISION</span>
                </div>
              </div>
            </div>
            
            {/* CTA Card */}
            <div className="glass-dark rounded-3xl p-10 shadow-xl border border-white/10">
              <h3 className="text-2xl font-bold text-slate-100 mb-6">Ready to Get Started?</h3>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Transform your credit risk management with intelligent, data-driven insights.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a 
                  href="#get-started" 
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-lg hover:shadow-blue-500/50"
                >
                  <span>Get Started</span>
                  <ChevronRight size={18} />
                </a>
                <a 
                  href="#demo" 
                  className="inline-flex items-center gap-2 glass-dark hover:bg-white/10 px-6 py-3 rounded-xl font-semibold text-slate-200 transition-all border border-white/10"
                >
                  Request a Demo
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Indicators */}
        <div className="glass-dark rounded-3xl p-12 mb-16 text-center shadow-lg border border-white/10">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-blue-300">
              <Shield size={16} />
              <span className="mono">TRUSTED BY ENTERPRISES</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100">
              Built for Scale. Designed for Trust.
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Helping businesses across India make smarter credit decisions with transparent, 
              explainable, and actionable intelligence.
            </p>
          </div>
        </div>
        
        <div className="text-center">
          <div className="inline-flex items-center gap-3 text-slate-400 mono text-sm">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
            <span>PCRA Technologies • 2025</span>
          </div>
        </div>
      </div>
      
      <Footer/>
    </div>
  );
};